import React from 'react';

import { LAYER, useSmartClosePopups } from '@core/js/components/common/PopupManager';

import {
    TermsCheckbox
} from '@interlayer/js/app/components/main/Registration/components/fields/PreferencesFields/components/TermsCheckbox';
import { MarketingPreferencesFields, useSaveMarketingPreferences } from './components/MarketingPreferencesFields';

import { Button } from 'js/components/common/Button';
import Form from 'js/components/common/Form';
import { useConfirmationPopup } from 'js/components/common/popups/ConfirmationPopup';

import { setPlayerTags } from '@core/js/hooks/user/server';

import {
    CONFIRM_MARKETING_CASINO_OPTIN_ID,
    DEFAULT_MARKETING_PRODUCTS,
    MAIN_CONTACT_TYPES_FRONT
} from '@interlayer/js/app/components/main/MarketingPreferences/constants';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useTranslations } from 'js/hooks/i18n';
import { htcmdService } from 'js/services/htcmd.service';
import { required } from 'js/utils/Validation/validationRules';

import { useInitialMarketingValues } from './hooks/useInitialMarketingValues';

import style from './MarketingPreferences.scss';

let localFormChanges = {};

const getAllContactKeys = () => MAIN_CONTACT_TYPES_FRONT.reduce((acc, contactType) => {
    DEFAULT_MARKETING_PRODUCTS.forEach(product => {
        acc.push(`${product}_${contactType}`);
    });

    return acc;
}, []);

export const MarketingPreferences = ({
    products,
    onSuccess,
    showTerms = false,
    isConfirmPopup = false,
    tSuffix = '',
    ...other
}) => {
    const t = useTranslations(`playtech.mobile.system.marketing-preferences.${tSuffix}`);
    const initialValues = useInitialMarketingValues();
    const saveMarketingPreferences = useSaveMarketingPreferences(products);
    const showConfirmationPopup = useConfirmationPopup();
    const closePopups = useSmartClosePopups();

    const [
        dTagVerifyMarketingPreferencesPlayerTag = 'Athena/marketingprefs'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.verifyMarketingPreferencesPlayerTag
    ]);

    const validationConfig = {
        terms: [
            required({ error: 'playtech.mobile.system.marketing-preferences.tooltip.terms.required' })
        ]
    };
    const isTermsCheckboxVisible = showTerms || other.portletPreferences?.sendNativeClose;

    const applyChanges = valuesToSend => {
        return saveMarketingPreferences(valuesToSend).then(() => {
            const enabledClosing = other.portletPreferences?.sendNativeClose;

            setPlayerTags([{ name: dTagVerifyMarketingPreferencesPlayerTag }]).then(() => {
                if (enabledClosing && htcmdService.isEnabled) {
                    htcmdService.sendMessage('close');
                }

                localFormChanges = {};
                onSuccess && onSuccess();
            });
        });
    };

    const onSubmit = (valuesToSend) => {
        localFormChanges = { ...valuesToSend };

        const isAllTurnedOff = getAllContactKeys().every(key => !valuesToSend[key]);

        const shouldShowConfirmPopup = isConfirmPopup || other.portletPreferences?.shouldShowConfirmPopup;
        if (shouldShowConfirmPopup && isAllTurnedOff) {
            return new Promise((resolve, reject) => {
                showConfirmationPopup({
                    isModal: true,
                    id: CONFIRM_MARKETING_CASINO_OPTIN_ID,
                    layer: LAYER.HIGH_PRIORITY,
                    addFirst: true,
                    classNames: {
                        verticalButtons: style.modalBtnWrap,
                        content: style.content,
                        inner: style.content
                    },
                    title: 'playtech.mobile.system.marketing-preferences.optoutpopup.title',
                    content: 'playtech.mobile.system.marketing-preferences.optoutpopup.text',
                    buttons: [
                        {
                            onClick: () => {
                                applyChanges(valuesToSend)
                                    .then(() => resolve())
                                    .catch(() => reject())
                                    .finally(() => closePopups(CONFIRM_MARKETING_CASINO_OPTIN_ID));
                            },
                            text: t('playtech.mobile.system.marketing-preferences.optoutpopup.confirm'),
                            view: 'primary',
                            size: 'medium',
                            className: style.modalBtn
                        }, {
                            onClick: () => {
                                closePopups(CONFIRM_MARKETING_CASINO_OPTIN_ID);
                                return reject();
                            },
                            text: t('playtech.mobile.system.marketing-preferences.optoutpopup.decline'),
                            view: 'primary',
                            size: 'medium',
                            className: style.modalBtn
                        }
                    ]
                });
            });
        }

        return applyChanges(valuesToSend);
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{
                ...initialValues,
                ...localFormChanges
            }}
            validationConfig={isTermsCheckboxVisible ? validationConfig : {}}
            render={({ submitting, values, handleSubmit }) => (

                <form
                    onSubmit={handleSubmit}
                    className={style.container}
                >

                    <MarketingPreferencesFields products={products} tSuffix={tSuffix} />

                    {isTermsCheckboxVisible && (
                        <TermsCheckbox isInConfirmPopup />
                    )}

                    <Button
                        type="submit"
                        disabled={submitting || isTermsCheckboxVisible && !values[VALIDATION_FIELDS.terms]}
                        className={style.button}
                    >
                        {t('button.submit')}
                    </Button>
                </form>
            )}
        />
    );
};

export default MarketingPreferences;
